import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MapPickerModal from './MapPickerModal';
import { Box, IconButton, Paper, InputBase, Tooltip, Chip, Typography } from '@mui/material';
import { TransparentMenu, TPMenuList, TPItem } from '../ui/Modal/TransparentPortal';
import { alpha } from '@mui/material/styles';
import { Plus, Camera, Image as ImageIcon, FileUp, MapPin, Smile, Send as SendIcon, UserRound, X as CloseIcon, Trash2 as TrashIcon, Forward as ForwardIcon } from 'lucide-react';
import { useConciergeStore } from '../../store/conciergeStore';
import { uploadAttachment } from '../../api/conciergeApi';
import ConfirmDialog from '../ui/Modal/ConfirmDialog';

/**
 * ConciergeComposer – pill-style composer with accessory icons and a round send button.
 * Props:
 *  - propertyId?: string (defaults to localStorage.activePropertyId)
 *  - thread?: { userUid: string } (if omitted, will use store.activeThread)
 *  - autoFocus?: boolean (default: false)
 */
export default function ConciergeComposer({ propertyId: propPropertyId, thread: propThread, autoFocus = false, selectionActive: selectionActiveProp, selectedIds: selectedIdsProp }) {
  const {
    activeThread,
    sendMessage,
    addTempMessage,
    failTempMessage,
    replyTargetByThread,
    setReplyTarget,
    clearReplyTarget,
    editingByThread,
    setEditDraft,
    cancelEditMessage,
    submitEditMessage,
    // selection mode
    isSelectionMode,
    getSelection,
    deleteMessage,
    deleteSelectedMessages,
    clearSelection,
    exitSelectionMode,
  } = useConciergeStore();
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // staged attachments before send
  const [files, setFiles] = useState([]); // File[]
  const textareaRef = useRef(null);
  const imageInputRef = useRef(null);     // picks from photo library (images only)
  const cameraInputRef = useRef(null);    // opens device camera
  const anyFileInputRef = useRef(null);   // picks any file type
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const composerMountedRef = useRef(false);
  const [mapOpen, setMapOpen] = useState(false);

  const propertyId = propPropertyId || (typeof localStorage !== 'undefined' && localStorage.getItem('activePropertyId'));
  const thread = propThread || activeThread;

  const threadKey = useMemo(() => {
    if (!propertyId || !thread?.userUid) return null;
    return `${propertyId}:${thread.userUid}`;
  }, [propertyId, thread]);
  const replyTarget = useMemo(() => {
    return threadKey ? (replyTargetByThread?.[threadKey] || null) : null;
  }, [replyTargetByThread, threadKey]);

  const editState = useMemo(() => (threadKey ? (editingByThread?.[threadKey] || null) : null), [editingByThread, threadKey]);

  const selectedIdsRaw = useMemo(() => (
    propertyId && thread?.userUid ? (getSelection?.(propertyId, thread.userUid) || []) : []
  ), [getSelection, propertyId, thread]);

  const selectionActiveComputed = useMemo(() => {
    const mode = propertyId && thread?.userUid && isSelectionMode?.(propertyId, thread.userUid);
    return Boolean(mode || (selectedIdsRaw?.length > 0));
  }, [propertyId, thread, isSelectionMode, selectedIdsRaw]);

  const selectedIds = Array.isArray(selectedIdsProp) ? selectedIdsProp : selectedIdsRaw;
  const selectionActive = (typeof selectionActiveProp === 'boolean') ? selectionActiveProp : selectionActiveComputed;


  useEffect(() => {
    composerMountedRef.current = true;
    return () => { composerMountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (editState && composerMountedRef.current) {
      setText(editState.draft || '');
      setTimeout(() => { try { textareaRef.current?.focus(); } catch (_) {} }, 0);
    }
  }, [editState]);

  const canSend = useMemo(() => {
    const hasText = text.trim().length > 0;
    const hasFiles = files.length > 0;
    if (editState) {
      // while editing, require text and disallow files
      return Boolean(propertyId && thread?.userUid && hasText);
    }
    return Boolean(propertyId && thread?.userUid && (hasText || hasFiles));
  }, [propertyId, thread, text, files, editState]);

  const onPick = useCallback((e) => {
    if (editState) return;
    const picked = Array.from(e.target.files || []);
    if (picked.length) setFiles((prev) => [...prev, ...picked].slice(0, 5));
    e.target.value = null; // reset input
  }, [editState]);
  const removeFileAt = useCallback((idx) => setFiles((prev) => prev.filter((_, i) => i !== idx)), []);

  const openMenu = useCallback((e) => {
    if (editState) return;
    setAnchorEl(e.currentTarget);
  }, [editState]);
  const closeMenu = useCallback(() => setAnchorEl(null), []);

  const pickCamera = useCallback(() => { closeMenu(); setTimeout(() => cameraInputRef.current?.click(), 0); }, [closeMenu]);
  const pickImages = useCallback(() => { closeMenu(); setTimeout(() => imageInputRef.current?.click(), 0); }, [closeMenu]);
  const pickAnyFile = useCallback(() => { closeMenu(); setTimeout(() => anyFileInputRef.current?.click(), 0); }, [closeMenu]);

  const addMapAttachment = useCallback(() => {
    closeMenu();
    setMapOpen(true);
  }, [closeMenu]);

  const handleMapConfirm = useCallback((loc) => {
    // Append a structured location snippet so the bubble parser can render a map
    const { title, address, lat, lng, url } = loc || {};
    const label = (title && String(title).trim()) || 'Location';
    const lines = [
      label,
      ...(address ? [String(address).trim()] : []),
      (lat != null && lng != null) ? `${lat}, ${lng}` : null,
      url || null,
    ].filter(Boolean);

    setText((t) => (t ? `${t}\n` : '') + lines.join('\n'));
  }, []);

  const autoGrow = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const max = 6 * 22; // ~6 lines x line-height
    el.style.height = Math.min(el.scrollHeight, max) + 'px';
  }, []);

  useEffect(() => { autoGrow(); }, [text, autoGrow]);

  const cancelReply = useCallback(() => {
    if (threadKey) clearReplyTarget(propertyId, thread.userUid);
  }, [threadKey, clearReplyTarget, propertyId, thread]);

  const cancelEditAndClear = useCallback(() => {
    if (thread?.userUid && propertyId) {
      cancelEditMessage({ propertyId, userUid: thread.userUid });
    }
    setText('');
  }, [cancelEditMessage, propertyId, thread]);


  const handleOpenConfirmDelete = useCallback(() => {
    if (!selectionActive || !selectedIds?.length) return;
    setConfirmOpen(true);
  }, [selectionActive, selectedIds]);

  const handleConfirmDelete = useCallback(async () => {
    try {
      if (typeof deleteSelectedMessages === 'function') {
        await deleteSelectedMessages({ propertyId, userUid: thread.userUid, ids: selectedIds, hardDelete: true, deleteAttachments: true });
      } else {
        for (const id of selectedIds) {
          try {
            await deleteMessage({ propertyId, userUid: thread.userUid, messageId: id, hardDelete: true, deleteAttachments: true });
          } catch (e) {
          }
        }
      }
    } finally {
      setConfirmOpen(false);
      try { clearSelection(propertyId, thread.userUid); } catch {}
      try { exitSelectionMode?.(propertyId, thread.userUid); } catch {}
    }
  }, [selectedIds, deleteSelectedMessages, deleteMessage, propertyId, thread, clearSelection, exitSelectionMode]);

  const doSend = useCallback(async () => {
    if (!canSend) return;
    setSending(true);
    const trimmed = text.trim();

    // --- EDIT MODE ---
    if (editState) {
      try {
        await submitEditMessage({ propertyId, userUid: thread.userUid });
        setText('');
        // if replying was open, close it (edit and reply shouldn't combine)
        if (threadKey) clearReplyTarget(propertyId, thread.userUid);
        cancelEditAndClear();
      } catch (e) {
      } finally {
        setSending(false);
      }
      return;
    }

    // --- SEND NEW MESSAGE (optimistic flow) ---
    setSending(true);
    const localAtts = files.map((f) => {
      const isImage = typeof f.type === 'string' && f.type.startsWith('image/');
      const u = URL.createObjectURL(f);
      return {
        url: u,
        thumbnailUrl: isImage ? u : undefined,
        type: isImage ? 'image' : 'file',
        name: f.name,
        size: f.size,
      };
    });
    
    try {
      let me = null;
      try { me = (typeof useConciergeStore !== 'undefined' && useConciergeStore.getState) ? useConciergeStore.getState().me : null; } catch (_) {}
      if (!me && typeof window !== 'undefined') {
        me = window.__EF_USER || window.__USER || null;
      }
      const senderObj = me ? { id: me._id || me.id, role: 'staff', displayName: me.fullName || me.name || me.username } : { role: 'staff' };

      // Prepare payload to send to store's sendMessage function
      const payload = {
        propertyId,
        userUid: thread.userUid,
        sender: senderObj,
        content: trimmed || (localAtts.length ? '(attachment)' : ''),
        attachments: localAtts,
        replyTo: replyTarget?.messageId,
        replySnapshot: replyTarget?.snapshot,
      };

      const attachments = [];
      for (const f of files) {
        const att = await uploadAttachment({ propertyId, userUid: thread.userUid, file: f });
        attachments.push(att);
      }

      const finalPayload = { ...payload, attachments };

      setText('');
      setFiles([]);

      await sendMessage(finalPayload);
      cancelReply();

      try { localAtts.forEach((a) => a?.url && URL.revokeObjectURL(a.url)); } catch (_) {}
    } catch (e) {
      try { failTempMessage({ propertyId, userUid: thread.userUid }); } catch {}
      try { cancelReply(); } catch {}
    } finally {
      setSending(false);
    }
  }, [canSend, files, propertyId, thread, text, sendMessage, failTempMessage, replyTarget, cancelReply, editState, submitEditMessage, threadKey, clearReplyTarget, cancelEditAndClear]);

  const onKeyDown = (e) => {
    // Enter to send, Shift+Enter for newline
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      doSend();
    }
  };

  if (selectionActive) {
    const count = selectedIds.length;
    return (
      <Box sx={{ p: 2, position: 'sticky', bottom: 0, zIndex: 3, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider', boxShadow: '0 -4px 14px rgba(0,0,0,0.06)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Tooltip title="Delete selected">
            <span>
              <IconButton size="small" color="error" onClick={handleOpenConfirmDelete} disabled={!count}>
                <TrashIcon size={18} />
              </IconButton>
            </span>
          </Tooltip>
          <Chip size="small" color="warning" variant="outlined" label={`${count} ${count === 1 ? 'message' : 'messages'} selected`} />
          <Tooltip title="Forward (coming soon)">
            <span>
              <IconButton size="small" disabled>
                <ForwardIcon size={18} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <ConfirmDialog
          open={confirmOpen}
          title={count === 1 ? 'Delete message?' : 'Delete messages?'}
          description={`Delete the selected ${count === 1 ? 'message' : 'messages'}. This can’t be undone.`}
          confirmLabel="Delete"
          cancelText="Cancel"
          onCancel={() => setConfirmOpen(false)}
          onConfirm={handleConfirmDelete}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider' }}>
      {/* Camera (capture) */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        disabled={!!editState}
        onChange={onPick}
      />
      {/* Photo library (images only) */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        disabled={!!editState}
        onChange={onPick}
      />
      {/* Any file type */}
      <input
        ref={anyFileInputRef}
        type="file"
        // allow anything; backend will treat non-images as files
        multiple
        hidden
        disabled={!!editState}
        onChange={(e) => {
          if (editState) return;
          const picked = Array.from(e.target.files || []);
          if (picked.length) setFiles((prev) => [...prev, ...picked].slice(0, 5));
          e.target.value = null;
        }}
      />

      {editState && (
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          mb: 1, px: 1.25, py: 0.75,
          borderRadius: 1,
          border: '1px solid', borderColor: 'warning.light',
          bgcolor: '#FFF7ED',
        }}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontSize: 12, color: 'text.secondary', mb: 0.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Editing message
            </Box>
            <Box sx={{ fontSize: 12, color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {((editState.original?.text || editState.original?.content || '')).slice(0, 140)}
            </Box>
          </Box>
          <IconButton size="small" onClick={cancelEditAndClear}>
            <CloseIcon size={14} />
          </IconButton>
        </Box>
      )}

      {replyTarget && (
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          mb: 1, px: 1.25, py: 0.75,
          borderRadius: 1,
          border: '1px solid', borderColor: 'divider',
          bgcolor: '#F8FAFC',
        }}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontSize: 12, color: 'text.secondary', mb: 0.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Replying to {replyTarget?.snapshot?.sender || 'message'}
            </Box>
            <Box sx={{ fontSize: 12, color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {replyTarget?.snapshot?.text || '(attachment)'}
            </Box>
          </Box>
          <IconButton size="small" onClick={cancelReply}>
            <CloseIcon size={14} />
          </IconButton>
        </Box>
      )}

      {/* attachment previews */}
      {files.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          {files.map((f, i) => {
            const isImage = typeof f.type === 'string' && f.type.startsWith('image/');
            const objUrl = URL.createObjectURL(f);
            return (
              <Box key={i} sx={{ position: 'relative' }}>
                {isImage ? (
                  <img
                    src={objUrl}
                    alt={f.name}
                    style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, boxShadow: '0 2px 6px rgba(0,0,0,0.1)' }}
                  />
                ) : (
                  <Paper
                    elevation={0}
                    sx={{
                      width: 200,
                      height: 56,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1,
                      borderRadius: 1.25,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Box sx={{ width: 36, height: 36, borderRadius: 1, border: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FileUp size={16} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" noWrap title={f.name}>{f.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {Math.ceil((f.size || 0) / 1024)} KB
                      </Typography>
                    </Box>
                  </Paper>
                )}
                <IconButton
                  size="small"
                  onClick={() => removeFileAt(i)}
                  sx={{ position: 'absolute', top: -10, right: -10, bgcolor: 'white', boxShadow: 1 }}
                >
                  <CloseIcon size={14} />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
        {/* pill composer */}
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.25,
            py: 0.75,
            borderRadius: 999,
            border: '1px solid',
            borderColor: 'rgba(0,0,0,0.12)',
            bgcolor: '#F8FAFC',
          }}
          onDragOver={(e) => {
            if (!thread || !!editState) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
          }}
          onDrop={(e) => {
            if (!thread || !!editState) return;
            e.preventDefault();
            const picked = Array.from(e.dataTransfer?.files || []);
            if (picked.length) setFiles((prev) => [...prev, ...picked].slice(0, 5));
          }}
        >
          <Tooltip title="Add">
            <span>
              <IconButton size="small" onClick={openMenu} disabled={!thread || sending || !!editState} sx={{ color: '#64748B' }}>
                <Plus size={18} />
              </IconButton>
            </span>
          </Tooltip>

          <TransparentMenu
            open={menuOpen}
            onClose={closeMenu}
            anchorEl={anchorEl}
            minWidth={220}
            padding={0.5}
            blur={18}
            opacityTop={0.24}
            opacityBottom={0.10}
            borderAlpha={0.28}
          >
            <TPMenuList sx={{ minWidth: 220 }}>
              <TPItem icon={<ImageIcon size={18} />} label="Photo" onClick={pickImages} />
              <TPItem icon={<FileUp size={18} />}  label="File"  onClick={pickAnyFile} />
              <TPItem icon={<Camera size={18} />}  label="Camera" onClick={pickCamera} />
              <TPItem icon={<MapPin size={18} />}  label="Location" onClick={addMapAttachment} />
            </TPMenuList>
          </TransparentMenu>

          <InputBase
            inputRef={textareaRef}
            autoFocus={autoFocus}
            value={text}
            onChange={(e) => {
              const v = e.target.value;
              setText(v);
              if (editState) {
                setEditDraft({ propertyId, userUid: thread?.userUid, text: v });
              }
            }}
            onInput={autoGrow}
            onKeyDown={onKeyDown}
            placeholder={
              !thread
                ? 'Select a conversation to start…'
                : editState
                  ? 'Edit your message…'
                  : (replyTarget ? 'Reply with a message…' : 'Type a message here…')
            }
            disabled={!thread}
            multiline
            inputProps={{ id: 'concierge-composer-input' }}
            sx={{
              flex: 1,
              px: 0.5,
              fontSize: 15,
              lineHeight: '22px',
              maxHeight: 22 * 6,
            }}
            onPaste={(e) => {
              try {
                if (editState) return;
                const items = e.clipboardData?.items || [];
                const incoming = [];
                for (const it of items) {
                  if (it.kind === 'file') {
                    const f = it.getAsFile?.();
                    if (f) incoming.push(f);
                  }
                }
                if (incoming.length) {
                  e.preventDefault();
                  setFiles((prev) => [...prev, ...incoming].slice(0, 5));
                }
              } catch {}
            }}
          />

          <Tooltip title="Emoji">
            <span>
              <IconButton size="small" disabled sx={{ color: '#64748B' }}>
                <Smile size={18} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Guest">
            <span>
              <IconButton size="small" disabled sx={{ color: '#64748B' }}>
                <UserRound size={18} />
              </IconButton>
            </span>
          </Tooltip>
        </Paper>

        {/* round gradient send */}
        <IconButton
          onClick={doSend}
          disabled={!canSend || sending}
          sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            color: 'white',
            background: canSend
              ? 'linear-gradient(135deg, #bd9908ff 0%, #8f6c02ff 100%)'
              : 'linear-gradient(135deg, #CBD5E1 0%, #E2E8F0 100%)',
            boxShadow: canSend ? '0 8px 16px rgba(150, 114, 6, 0.35)' : 'none',
            '&:hover': {
              background: 'linear-gradient(135deg, #8b6906ff 0%, #a16f05ff 100%)',
            },
          }}
        >
          <SendIcon size={18} />
        </IconButton>
      </Box>
      <MapPickerModal
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        onConfirm={(loc) => { handleMapConfirm(loc); setMapOpen(false); }}
        gKey={
          // CRA-style envs (rebuild needed after change)
          (typeof process !== 'undefined' && process.env && (
            process.env.REACT_APP_GOOGLE_MAPS_KEY ||
            process.env.REACT_APP_GOOGLE_STATIC_MAPS_KEY
          )) ||
          // optional globals (window.__GMAPS_KEY__)
          (typeof window !== 'undefined' && (window.__GMAPS_KEY__ || window.__GOOGLE_MAPS_KEY__)) ||
          // localStorage fallbacks used during manual testing
          (typeof localStorage !== 'undefined' && (localStorage.getItem('googleMapsKey') || localStorage.getItem('googleStaticKey'))) ||
          undefined
        }
        mbx={typeof localStorage !== 'undefined' ? localStorage.getItem('mapboxToken') || undefined : undefined}
      />
    </Box>
  );
}
  